/**
 * Backups card (Settings) — display rules.
 *
 * Turns the `/api/backups/status` payload into the text the editor
 * reads. Pure and framework-free —
 * node --test frontend/scripts/backupView.test.mjs
 */

/** 1536 -> "1.5 KB". */
export function formatBytes(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return "0 KB";
  if (v < 1024 * 1024) return `${Math.max(0.1, v / 1024).toFixed(1)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(1)} MB`;
  return `${(v / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** 75.4 -> "1 min 15 s". */
export function formatDuration(seconds) {
  const s = Math.round(Number(seconds));
  if (!Number.isFinite(s) || s < 0) return "";
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  if (m < 60) return rest ? `${m} min ${rest} s` : `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${m % 60} min`;
}

const count = (n, one, many) => {
  const v = Number(n) || 0;
  return `${v.toLocaleString("en-US")} ${v === 1 ? one : many}`;
};

/** "1,204 records · 318 images (2 new) · 4.1 MB" for a run summary. */
export function summaryLine(summary) {
  const s = summary || {};
  const parts = [count(s.records, "record", "records")];
  if (s.files_total !== undefined) {
    const stored = (Number(s.files_already) || 0) + (Number(s.files_new) || 0);
    parts.push(
      `${count(stored, "image", "images")}${
        s.files_new ? ` (${Number(s.files_new).toLocaleString("en-US")} new)` : ""
      }`
    );
  }
  parts.push(formatBytes(s.data_bytes));
  return parts.join(" · ");
}

/** "ok" | "partial" | "error" | "running" | "never" */
export function tone(state) {
  const st = state?.last_status;
  if (st === "running" || st === "ok" || st === "partial" || st === "error") return st;
  return "never";
}

export const TONE_LABEL = {
  ok: "Backed up",
  partial: "Backed up, with notes",
  error: "Last run failed",
  running: "Running now",
  never: "No backup yet",
};

/**
 * Hours since the last complete backup, or null when there has never
 * been one. A nightly job that is healthy is never more than ~24 h old.
 */
export function hoursSinceSuccess(state, now = Date.now()) {
  const t = Date.parse(state?.last_success_at || "");
  if (Number.isNaN(t)) return null;
  return Math.max(0, (now - t) / 3600000);
}

/** True when the last good backup is old enough to worry about. */
export function isOverdue(state, now = Date.now(), limitHours = 36) {
  const h = hoursSinceSuccess(state, now);
  return h !== null && h > limitHours;
}

/** What to tell the editor when backups are not configured. */
export function setupMessage(status) {
  const s = status || {};
  if (s.configured) return "";
  if (s.problem) return s.problem;
  const missing = Array.isArray(s.missing) ? s.missing : [];
  return missing.length
    ? `Backups are not set up yet. Missing on the server: ${missing.join(", ")}.`
    : "Backups are not set up yet.";
}

/** Can the "Back up now" button be pressed? */
export function canRun(status, isAdmin, pending = false) {
  return !!(
    isAdmin &&
    status?.configured &&
    !pending &&
    tone(status?.property) !== "running"
  );
}

/** Quiet lines for the card: true, but nothing to act on. */
export function infoLines(summary) {
  const info = summary?.info;
  return Array.isArray(info) ? info.filter((x) => typeof x === "string" && x) : [];
}

// ---------------------------------------------------------------------
// Restore (Admin page)
// ---------------------------------------------------------------------

/** "20261007T083039Z" -> "2026-10-07T08:30:39Z" ("" when not an id). */
export function snapshotIso(id) {
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(String(id || ""));
  if (!m) return "";
  const iso = `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}Z`;
  return Number.isNaN(Date.parse(iso)) ? "" : iso;
}

/** What to call the database a backup came from. */
export function installationName(installation, currentId) {
  const inst = installation || {};
  const label = String(inst.label || "").trim();
  const id = String(inst.id || "");
  if (id && id === currentId) return label ? `${label} (this installation)` : "This installation";
  if (label) return label;
  if (id) return `Installation ${id.slice(0, 6)}`;
  return "Unknown installation";
}

/** Did this backup come from the database we are restoring into? */
export function sameInstallation(row, currentId) {
  const id = row?.installation?.id || "";
  return !!id && id === currentId;
}

/**
 * Starting options for a property restore. Connections default ON only
 * when the backup came from THIS installation: restored anywhere else,
 * a second app would hold live WordPress and Facebook credentials.
 * Schedules always start off — a restored pending post must not fire
 * on the scheduler's next tick.
 */
export function restoreDefaults(row, currentId) {
  return {
    restore_connections: sameInstallation(row, currentId),
    keep_schedules: false,
    include_history: true,
  };
}

/** Replacing a property that exists here needs its name typed back. */
export function typedNameOk(row, typed) {
  if (!row?.exists_here) return true;
  const want = String(row.name_here || "").trim().toLowerCase();
  return !!want && String(typed || "").trim().toLowerCase() === want;
}

export function canStartRestore({ row, snapshotId, typed, busy }) {
  return !!(row && snapshotId && !busy && typedNameOk(row, typed));
}

const JOB_NAME = {
  verify: "Check",
  restore_property: "Restore property",
  restore_accounts: "Restore accounts",
  restore_images: "Copy images",
};

export function jobName(job) {
  return JOB_NAME[job?.kind] || "Job";
}

/** One sentence on how a finished (or running) job went. */
export function jobLine(job) {
  const j = job || {};
  if (j.status === "running") {
    return [j.stage, j.progress].filter(Boolean).join(" · ") || "Starting";
  }
  if (j.status === "error") return j.error || "Failed";
  const s = j.summary || {};
  const img = (n, one, many) => `${count(n, "image", "images")} ${Number(n) === 1 ? one : many}`;
  switch (j.kind) {
    case "verify": {
      const parts = [`${count(s.records, "record", "records")} intact`];
      if (s.images !== undefined) parts.push(`${count(s.images - (s.images_absent || 0), "image", "images")} in the bucket`);
      return parts.join(" · ");
    }
    case "restore_property": {
      const parts = [
        `${s.created ? "Created" : "Replaced"} ${s.property_name || "the property"}`,
        count(s.records, "record", "records"),
      ];
      if (s.images_copied) parts.push(img(s.images_copied, "copied back", "copied back"));
      if (s.schedules_made_inert) {
        parts.push(`${count(s.schedules_made_inert, "schedule", "schedules")} switched off`);
      }
      parts.push(s.connections_restored ? "connections restored" : "connections left blank");
      return parts.join(" · ");
    }
    case "restore_accounts":
      return [
        `${count(s.users_added, "account", "accounts")} added`,
        `${count(s.users_kept, "account", "accounts")} already here`,
        `${count((s.settings_added || 0) + (s.settings_replaced || 0), "shared setting", "shared settings")} restored`,
      ].join(" · ");
    case "restore_images":
      return [
        img(s.images_copied, "copied back", "copied back"),
        `${count(s.images_present, "image", "images")} already there`,
      ].join(" · ");
    default:
      return "Done";
  }
}

/** Yellow notes on a finished job. */
export function jobWarnings(job) {
  const w = job?.summary?.warnings;
  return Array.isArray(w) ? w : [];
}
